/* Warehouse Bold — variant selection for the WB product page.
 *
 * The WB buy panel rendered <variant-selects> with radio pills and a <select>,
 * but nothing loaded any JS to drive them: the base theme's variant-selects.js is only
 * loaded from snippets/product-variant-picker.liquid, and product-info.js only
 * from main-product / featured-product / quick-view — none of which the WB
 * template renders. So changing an option left price, stock, SKU, the CTA and
 * the hidden `name="id"` on the FIRST variant, and the wrong variant went into
 * the cart at the wrong price (Owen, on Malin lockwire, 2026-08-11).
 *
 * ⚠ Every string and every money value comes PRE-RENDERED from Liquid in the
 * [data-wb-variants] table. This file never formats currency and never writes
 * English. That is deliberate: the money format varies by market (comma
 * decimals, zero-decimal currencies) and the stock wording is translated into
 * de/es/fr/ko. wb-product.js hard-codes its dispatch strings and is wrong in
 * every non-English market as a result — do not copy that pattern.
 *
 * ⚠ The option inputs live OUTSIDE the <form>, so they never submit. The only
 * thing that reaches Shopify is the hidden `name="id"` input, which is why
 * keeping it in sync is the one non-cosmetic job here.
 */
(() => {
  if (window.__wbVariants) return;
  window.__wbVariants = true;

  const parse = (el) => {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  const mount = (root) => {
    if (root.__wbVarBound) return;
    const table = parse(root.querySelector('[data-wb-variants]'));
    if (!table || !table.length) return;
    root.__wbVarBound = true;

    const buy = root.closest('[data-wb-buy]') || document;
    const scope = root.closest('.wb-hero') || document;

    const q = (sel, from) => (from || buy).querySelector(sel);

    /* Selected value per option index, read from whichever control the option
       rendered as — pills above 7 values become a <select>. */
    const readSelection = () =>
      [...root.querySelectorAll('[data-wb-option]')].map((fs) => {
        const sel = fs.querySelector('[data-wb-opt-select]');
        if (sel) return sel.value;
        const checked = fs.querySelector('.wb-pill__input:checked');
        return checked ? checked.value : null;
      });

    const findVariant = (chosen) =>
      table.find((v) => v.options.length === chosen.length &&
        v.options.every((o, i) => o === chosen[i])) || null;

    /* A value is unavailable when NO variant containing it is buyable. With a
       single option that is exact; with several it is the standard optimistic
       reading (it ignores the other selected values), which is the same thing
       Shopify's own pickers do and never hides a reachable combination. */
    const markPills = () => {
      [...root.querySelectorAll('[data-wb-option]')].forEach((fs, idx) => {
        fs.querySelectorAll('.wb-pill__input').forEach((input) => {
          const any = table.some((v) => v.options[idx] === input.value && v.buyable);
          const label = fs.querySelector('label[for="' + CSS.escape(input.id) + '"]');
          if (label) label.classList.toggle('wb-pill--soldout', !any);
        });
      });
    };

    /* Jump the gallery by CLICKING the thumb that owns the variant's media,
       so the slider stays the one owner of its own scroll/transform state.
     *
     * ⚠ Retries until the click actually takes, rather than assuming it did.
     * wb-product.js binds the thumbnail handler on DOMContentLoaded and this
     * file mounts on the same event, so on the very first pass the click can
     * land on a thumb nothing is listening to yet and silently do nothing —
     * measured, not theoretical: a deep-linked variant sat on slide 0 while
     * the identical click a moment later moved it. Neither setTimeout 0 nor a
     * `load` listener fixed it reliably, because the binding order is what
     * varies, not the timing. Self-terminating: it stops the instant the
     * active class appears, and gives up after ~1.2s rather than spinning. */
    /* ⚠ `e.isTrusted` is load-bearing. Without it this handler fires on the
       synthetic click jumpGallery itself dispatches, marks the gallery as
       user-driven on the FIRST attempt, and every retry after that bails —
       which is exactly the bug it was added to avoid, inverted. Only a real
       pointer/keyboard event sets isTrusted. */
    let galleryTouched = false;
    scope.addEventListener('click', (e) => {
      if (!e.isTrusted) return;
      if (e.target.closest('[data-wb-thumbs], [data-wb-prev], [data-wb-next]')) galleryTouched = true;
    }, true);

    const jumpGallery = (v, tries, initial) => {
      /* Once the shopper has driven the gallery themselves, the opening
         deep-link sync stops chasing them. A variant CHANGE still moves it —
         that is a direct response to their own action. */
      if (initial && galleryTouched) return;
      if (!v || !v.media) return;
      const thumb = scope.querySelector('[data-wb-media="' + v.media + '"]');
      if (!thumb || thumb.classList.contains('is-active')) return;
      thumb.click();
      if (!thumb.classList.contains('is-active') && (tries || 0) < 40) {
        setTimeout(() => jumpGallery(v, (tries || 0) + 1, initial), 150);
      }
    };

    const apply = (v, initial) => {
      /* ---- price ---- */
      const price = q('[data-wb-price]');
      if (price && v) {
        price.setAttribute('data-unit-cents', v.cents);
        if (v.digits >= 3) price.setAttribute('data-digits', v.digits);
        else price.removeAttribute('data-digits');
        price.classList.toggle('is-sale', !!v.sale);

        const int = q('[data-wb-price-int]', price);
        const dec = q('[data-wb-price-cents]', price);
        const plain = q('[data-wb-price-plain]', price);
        if (int) int.textContent = v.int;
        if (dec) dec.textContent = v.dec;
        if (plain) plain.textContent = v.plain;

        /* was / save exist in the markup only when the FIRST variant was on
           sale, so they are created and removed here rather than toggled. */
        let was = price.querySelector('.wb-price__was');
        let save = price.querySelector('.wb-price__save');
        if (v.sale) {
          if (!was) { was = document.createElement('s'); was.className = 'wb-price__was'; price.appendChild(was); }
          was.textContent = v.was;
          if (!save) {
            save = document.createElement('span');
            save.className = 'wb-price__save';
            /* The word "Save" is not ours to invent — lift it off the label
               already on the page, keeping whatever locale rendered it. */
            save.dataset.wbSaveWord = (price.dataset.wbSaveWord || '');
            price.appendChild(save);
          }
          const word = price.dataset.wbSaveWord || '';
          save.textContent = word ? word + ' ' + v.save : v.save;
        } else {
          if (was) was.remove();
          if (save) save.remove();
        }
      }

      /* ---- sku ---- */
      const sku = q('.wb-sku');
      if (sku && v) {
        const label = sku.dataset.wbSkuLabel || sku.textContent.split(':')[0];
        sku.dataset.wbSkuLabel = label;
        sku.textContent = v.sku ? label + ': ' + v.sku : '';
        sku.hidden = !v.sku;
      }

      /* ---- approximate spool length ---- */
      const spool = q('[data-wb-spool]');
      if (spool && v) {
        const spoolLen = q('[data-wb-spool-len]', spool);
        if (spoolLen) spoolLen.textContent = v.len || '';
        /* Sizes the manufacturer publishes no length for hide the whole block,
           note included — a bare "approximate" caption with no figure reads
           like something failed to load. */
        spool.hidden = !v.len;
      }

      /* ---- stock row ---- */
      const stock = q('[data-wb-stock]');
      if (stock && v) {
        stock.className = stock.className.replace(/wb-stock--\S+/, 'wb-stock--' + v.state);
        const state = stock.querySelector('.wb-stock__state');
        if (state) state.innerHTML = v.stateHtml;
      }

      /* ---- Per-foot conversion (products sold by the metre) ----
         Pre-rendered per variant in Liquid, like every other money string
         here, so a multi-variant reel prices each option correctly and the
         currency formatting is never rebuilt in JS. */
      const ft = q('[data-wb-price-ft]', scope);
      if (ft && v && v.ft) ft.textContent = v.ft;

      /* ---- How to get it card ----
         Rows for EVERY state sit in the DOM; only `hidden` moves. Re-rendering
         was rejected on purpose: the countdown row is bound once by
         wb-product.js and the estimator holds live inputs plus whatever the
         customer already looked up, so replacing the markup would stop the
         clock and throw those results away.
         Scoped to `scope` (.wb-hero), not `buy` — the card sits outside the
         buy panel on some breakpoints. */
      const card = q('[data-wb-htgi]', scope);
      if (card && v) {
        const group =
          v.state === 'oos' ? 'oos' : v.state === 'backorder' ? 'backorder' : 'ok';
        card.querySelectorAll('[data-when]').forEach((el) => {
          /* Padded match so "ok" never matches inside "backorder". */
          const when = ' ' + (el.getAttribute('data-when') || '') + ' ';
          el.hidden = when.indexOf(' ' + group + ' ') === -1;
        });
      }

      /* ---- CTA + the hidden id that actually reaches the cart ---- */
      const cta = q('[data-wb-cta]');
      const ctaText = q('[data-wb-cta-text]');
      const idInput = q('.product-variant-id');
      const stepper = q('.wb-stepper');
      const qty = q('.wb-qty');

      if (ctaText && v) ctaText.textContent = v.cta;
      if (cta && v) {
        /* ⚠ Two different notions of "cannot buy". `soldout` disables the
           button outright; `buyable` is false for below-moq too, where the
           CTA stays live because enquiring is a real action. */
        cta.disabled = !!v.soldout;
        cta.classList.toggle('wb-cta--soldout', !!v.soldout);
        cta.classList.toggle('wb-cta--navy', !v.buyable && !v.soldout);
        /* Mirrors the server-rendered branch: an unbuyable state must not
           POST. type=button is what stops it — `data-wb-no-submit` only tells
           wb-product.js to skip the loading spinner. */
        cta.setAttribute('type', v.buyable ? 'submit' : 'button');
        if (v.buyable) {
          cta.removeAttribute('formnovalidate');
          cta.removeAttribute('data-wb-no-submit');
        } else {
          cta.setAttribute('formnovalidate', '');
          cta.setAttribute('data-wb-no-submit', '');
        }
      }
      if (idInput) {
        if (v) { idInput.value = v.id; idInput.disabled = !v.buyable; }
        else { idInput.disabled = true; }
      }
      if (stepper) stepper.classList.toggle('is-disabled', !v || !v.buyable);
      if (qty) qty.disabled = !v || !v.buyable;

      /* ---- sticky buy bar ---- */
      const bar = document.querySelector('[data-wb-bar-price]');
      if (bar && v) bar.textContent = v.plain;

      /* The bar is a mirror, so its CTA must follow the panel's — colour,
         disabled and label together. Nothing synced its class before, so
         switching to a sold-out option left the bar on the previous
         variant's styling. */
      const barCta = document.querySelector('[data-wb-bar-cta]');
      if (barCta && v) {
        barCta.disabled = !!v.soldout;
        barCta.classList.toggle('wb-cta--soldout', !!v.soldout);
        barCta.classList.toggle('wb-cta--navy', !v.buyable && !v.soldout);
      }
      /* ⚠ The LABEL is not set here. syncBar() in wb-product.js already owns
         that string — it is quantity-aware ("ADD 2 TO CART") and reads the
         panel CTA's live disabled flag — so this nudges it to recompute
         instead of writing the text twice from two places. Writing it here
         directly is what left the bar stuck on "SOLD OUT" after switching
         back to an in-stock option: the branch only fired while sold out and
         never restored. */
      const qtyEl = document.querySelector('.wb-qty');
      if (qtyEl) qtyEl.dispatchEvent(new Event('input', { bubbles: true }));

      /* ---- gallery ----
       * Only on a real change. The opening slide is NOT this file's job any
       * more and must not become it again.
       *
       * ⚠ History, so nobody re-adds the initial jump: clicking the variant's
       * thumb from here at load — immediately, on setTimeout, on `load`, or on
       * a 6-second retry loop — was measured landing back on slide 0 every
       * time. What varies is the order in which this file and wb-product.js
       * initialise, not the timing, so no amount of waiting wins it.
       *
       * That race is now gone rather than dodged: `?variant=` already reaches
       * the server, so wb-main-product.liquid renders `is-active` on the
       * selected variant's thumb and wb-product.js starts the track from
       * whichever thumb carries it. The page opens correct instead of being
       * corrected. A shopper-initiated CHANGE still moves the gallery from
       * here — that is a live response to their click, with both scripts long
       * since initialised. */
      if (!initial) jumpGallery(v, 0, false);

      /* ---- option value readouts in each legend ---- */
      const chosen = readSelection();
      [...root.querySelectorAll('[data-wb-option]')].forEach((fs, i) => {
        const out = fs.querySelector('[data-wb-opt-value]');
        if (out && chosen[i]) out.textContent = chosen[i];
      });

      /* ---- address bar, so a copied link lands on the same variant ----
         Skipped on the initial pass: loading a plain product URL should not
         silently grow a query string the shopper never chose. */
      if (!initial && v && window.history && history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url);
      }

      /* Let the estimator (and anything else) re-quote for the new variant.
         wb-estimator.js keys its cache per variant, so this is what stops a
         0.020" quote being shown for a 0.032" spool. */
      if (v) {
        document.dispatchEvent(new CustomEvent('wb:variant:change', {
          detail: { variantId: v.id, available: v.buyable }
        }));
      }
    };

    const onChange = () => apply(findVariant(readSelection()));

    root.addEventListener('change', (e) => {
      if (e.target.matches('.wb-pill__input, [data-wb-opt-select]')) onChange();
    });

    markPills();

    /* ⚠ Run once for the variant the SERVER rendered, not just on change.
     *
     * Two things are wrong until this runs, and both are worst on a deep link
     * straight to an out-of-stock variant — the exact link a back-in-stock
     * email sends:
     *   · the CTA is type="submit" in the server markup for every state, so an
     *     unbuyable variant would POST and surface a raw Shopify cart error;
     *   · the base theme's own ProductForm constructor (assets/theme.js) sets
     *     `productIdInput.disabled = false` unconditionally, undoing the
     *     `disabled` the section rendered on the hidden variant id.
     * Re-applying through the same path that handles a change fixes both, and
     * also moves the gallery to the deep-linked variant's image, which the
     * server does not do — it always renders slide 0 active.
     *
     * Deferred a tick so it lands after theme.js's constructor rather than
     * racing it. */
    const initialVariant = findVariant(readSelection());
    setTimeout(() => apply(initialVariant, true), 0);

  };

  const init = () => {
    document.querySelectorAll('variant-selects.wb-variants').forEach(mount);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
